import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { FileCheck2, CalendarClock, FileSignature, BadgeCheck, AlertTriangle, Loader2 } from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, SectionTitle } from "@/components/ChartCard";
import { fmtInt, fmtPct, monthLabel, CHART, tooltipStyle } from "@/lib/format";

const tone = (v) => (v >= 90 ? "good" : v >= 80 ? "warn" : "bad");

const Supplier = () => {
  const { data, isLoading } = useAnalytics("supplier", "/analytics/supplier");
  if (isLoading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const k = data.kpis;
  const trend = data.trend.map((t) => ({ ...t, label: monthLabel(t.month) }));

  return (
    <div>
      <SectionTitle
        title="Supplier Management"
        description="PO handover, bookings, documentation and order performance by supplier"
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard testId="kpi-po" label="PO Handover" value={fmtPct(k.po_handover)} icon={FileCheck2} tone={tone(k.po_handover)} info="% of purchase orders handed over by the supplier on or before the agreed cut-off date." />
        <KpiCard testId="kpi-booking" label="On-Time Booking" value={fmtPct(k.on_time_booking)} icon={CalendarClock} tone={tone(k.on_time_booking)} info="% of shipments booked with the carrier within the agreed booking window." />
        <KpiCard testId="kpi-dochandover" label="Doc Handover" value={fmtPct(k.doc_handover)} icon={FileSignature} tone={tone(k.doc_handover)} info="% of shipments where shipping documents were submitted on time." />
        <KpiCard testId="kpi-docacc" label="Doc Accuracy" value={fmtPct(k.doc_accuracy)} icon={BadgeCheck} tone={tone(k.doc_accuracy)} info="% of shipping documents received complete and error-free." />
        <KpiCard testId="kpi-lateorders" label="Late Orders" value={fmtInt(k.late_orders)} sub={`${k.late_orders_pct}% of orders`} icon={AlertTriangle} tone={k.late_orders_pct <= 15 ? "good" : k.late_orders_pct <= 30 ? "warn" : "bad"} info="Number of orders delivered after the customer's Required Delivery Date (RDD)." />
      </div>

      <div className="mt-6">
        <ChartCard testId="chart-supplier-trend" title="Supplier Performance Trend" subtitle="Monthly on-time / accuracy percentages">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend} margin={{ left: -18, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 100]} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="po_handover" name="PO Handover" stroke={CHART.sea} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="on_time_booking" name="On-Time Booking" stroke={CHART.air} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="doc_handover" name="Doc Handover" stroke={CHART.road} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="doc_accuracy" name="Doc Accuracy" stroke={CHART.lcl} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card card-elevated">
        <div className="border-b border-border/70 px-5 py-4">
          <h3 className="text-sm font-semibold tracking-tight">Supplier League Table</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Ranked by shipment volume</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="supplier-league-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-medium">Supplier</th>
                <th className="px-5 py-3 text-right font-medium">Shipments</th>
                <th className="px-5 py-3 text-right font-medium">PO Handover</th>
                <th className="px-5 py-3 text-right font-medium">Doc Accuracy</th>
                <th className="px-5 py-3 text-right font-medium">On-Time Del.</th>
                <th className="px-5 py-3 text-right font-medium">Late Orders</th>
              </tr>
            </thead>
            <tbody>
              {data.league.map((r) => (
                <tr key={r.shipper} className="border-b border-border/60 transition-colors hover:bg-secondary/50">
                  <td className="px-5 py-3 font-medium">{r.shipper}</td>
                  <td className="px-5 py-3 text-right font-mono">{r.shipments}</td>
                  <td className="px-5 py-3 text-right font-mono">{r.po_handover}%</td>
                  <td className="px-5 py-3 text-right font-mono">{r.doc_accuracy}%</td>
                  <td className={`px-5 py-3 text-right font-mono ${r.on_time_delivery >= 80 ? "text-emerald-600" : r.on_time_delivery >= 60 ? "text-amber-600" : "text-red-600"}`}>
                    {r.on_time_delivery}%
                  </td>
                  <td className="px-5 py-3 text-right font-mono">{r.late_orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Supplier;
